import { Card, Empty, Spin, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import depositApi from '../../apis/deposit';
import {
  DepositTransactionType,
  IDepositTransaction,
} from '../../interfaces/Deposit';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';

// Configure dayjs for Vietnamese timezone
dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  margin-top: 20px;

  .ant-table-thead > tr > th {
    background-color: #fafafa;
    font-weight: 600;
  }
`;

const AmountText = styled(Text)<{ $type: DepositTransactionType }>`
  font-weight: 600;
  color: ${(props) =>
    props.$type === DepositTransactionType.DEPOSIT ? '#52c41a' : '#ff4d4f'};
`;

interface DepositTransactionHistoryProps {
  userId: string;
}

const DepositTransactionHistory: React.FC<DepositTransactionHistoryProps> = ({
  userId,
}) => {
  const [transactions, setTransactions] = useState<
    PaginationParamsResponse<IDepositTransaction>
  >({
    items: [],
    total: 0,
    page: 1,
    perPage: 10,
    pageCount: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const result = await depositApi.getDepositTransactions(userId, {
        page: transactions.page,
        perPage: transactions.perPage,
      });

      setTransactions(result);
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      setTransactions({
        items: [],
        total: 0,
        page: 1,
        perPage: 10,
        pageCount: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [userId, transactions.page, transactions.perPage]);

  useEffect(() => {
    if (userId) {
      fetchTransactions();
    }
  }, [fetchTransactions, userId]);

  const columns = [
    {
      title: 'Ngày giao dịch',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (date: string) => {
        // Try different parsing methods
        const parsed1 = dayjs(date);
        return parsed1.format('DD/MM/YYYY HH:mm');
      },
    },
    {
      title: 'Loại giao dịch',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type: DepositTransactionType) => (
        <Tag color={type === DepositTransactionType.DEPOSIT ? 'green' : 'red'}>
          {type === DepositTransactionType.DEPOSIT ? 'Nạp tiền' : 'Trừ tiền'}
        </Tag>
      ),
    },
    {
      title: 'Số tiền',
      dataIndex: 'amount',
      key: 'amount',
      width: 200,
      render: (amount: number, record: IDepositTransaction) => (
        <AmountText $type={record.type}>
          {record.type === DepositTransactionType.DEPOSIT ? '+' : '-'}
          {amount.toLocaleString('vi-VN')} VNĐ
        </AmountText>
      ),
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) =>
        notes || <Text type="secondary">Không có ghi chú</Text>,
    },
    {
      title: 'Người thực hiện',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
      render: (createdBy: any) => createdBy?.username || 'Hệ thống',
    },
  ];

  if (loading) {
    return (
      <StyledCard title="Lịch sử giao dịch">
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Đang tải lịch sử giao dịch...</div>
        </div>
      </StyledCard>
    );
  }

  return (
    <StyledCard
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Lịch sử giao dịch
          </Title>
          <Text type="secondary">
            Tổng cộng: {transactions.total} giao dịch
          </Text>
        </div>
      }
    >
      {transactions.items.length === 0 ? (
        <Empty
          description="Chưa có giao dịch nào"
          style={{ padding: '50px 0' }}
        />
      ) : (
        <Table
          columns={columns}
          dataSource={transactions.items}
          rowKey="id"
          pagination={{
            pageSize: transactions.perPage,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} giao dịch`,
            current: transactions.page,
            total: transactions.total,
            onChange: (page, pageSize) => {
              setTransactions({
                ...transactions,
                page,
                perPage: pageSize,
              });
            },
          }}
          scroll={{ x: 800 }}
        />
      )}
    </StyledCard>
  );
};

export default DepositTransactionHistory;
